// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import type { Paths } from '@shared/data-access/interfaces/routes/paths';
import { useSilverAlphaProposals } from '@client/silver-alpha';
import {
  useCallback,
  type FC,
  Fragment,
  type ReactElement,
  type ReactNode,
  useMemo,
  useState,
} from 'react';
import { AppLink } from '@client-app/routes/AppLink';
import { ReactComponent as LogoCitec } from '@client-app/assets/img/logo-citec.svg';
import { ReactComponent as LogoNuvara } from '@shared/ui-kit/assets/img/logo-nuvara360.svg';
import { AppIcon, type SvgIconName } from '@shared/ui-kit/components/app-icon';
import styles from './navigation-sidebar.module.css';
import { classNames } from '@shared/ui-kit/utils';
import { NavLink, useLocation } from 'react-router';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  BuildEnv,
  getBrandDomainByHostname,
  isAnyBuildEnv,
} from '@shared/environment';

type NavigationButtonElement = {
  id: string;
  type: 'button';
  component: ReactElement<any, any>;
  separator?: boolean;
  hidden?: boolean;
  gap?: boolean;
};

type NavigationLinkElement = {
  id: string;
  type: 'link';
  title: string;
  to: Paths;
  children?: Array<
    Pick<NavigationLinkElement, 'title' | 'to'> & {
      icon?: SvgIconName;
    }
  >;
  icon: SvgIconName;
  separator?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  description?: string;
  adornment?: ReactNode;
  gap?: boolean;
  parent?: boolean;
};

type NavigationSectionTitle = {
  id: string;
  type: 'section-title';
  title: string;
};

export type NavigationLink =
  | NavigationLinkElement
  | NavigationButtonElement
  | NavigationSectionTitle;

export interface NavigationSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NavigationSidebar: FC<NavigationSidebarProps> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const { hasProposals: showSilverAlpha } = useSilverAlphaProposals();

  const domain = useMemo(
    () =>
      typeof window === 'undefined'
        ? 'citec'
        : getBrandDomainByHostname(window.location.hostname),
    [],
  );

  const location = useLocation();

  const [expanded, setExpanded] = useState<string | false>(false);

  const onDrawerChange = useCallback(
    () => onOpenChange(!open),
    [onOpenChange, open],
  );

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const renderSectionTitle = (props: NavigationSectionTitle, key: number) => (
    <span
      key={key}
      className={styles['link-section-title']}
      aria-hidden={!open}
      data-testid={`navigation-sidebar-section-title-${props.id}`}
    >
      {props.title}
    </span>
  );

  const renderLink = (
    props: NavigationLinkElement,
    key: number,
    open: boolean,
    location: any,
  ) => (
    <Fragment key={key}>
      {props.gap && <hr className={classNames(styles['list-divider'], '!mt-20')} />}
      {props.children?.length && open ? (
        props.disabled ? (
          <Accordion
            classes={{ root: styles.accordion }}
            aria-disabled={props.disabled}
            disableGutters
            data-testid={`navigation-sidebar-accordion-${props.id}`}
            expanded={expanded === props.id}
            onChange={handleChange(props.id)}
            disabled={props.disabled}
          >
            <AccordionSummary
              id={props.to}
              classes={{
                root: classNames(styles['accordion-summary'], {
                  [styles['accordion-summary-active']]: location.pathname.includes(
                    props.to,
                  ),
                }),
                content: classNames(styles['accordion-summary-content']),
              }}
              aria-disabled={props.disabled}
            >
              <span className="flex flex-row items-center gap-x-4">
                <AppIcon icon={props.icon} size={16} />
                <span className={styles['link-title']}>{props.title}</span>
              </span>
            </AccordionSummary>
          </Accordion>
        ) : (
          <Accordion
            classes={{ root: styles.accordion }}
            aria-disabled={props.disabled}
            disableGutters
            data-testid={`navigation-sidebar-accordion-${props.id}`}
            expanded={expanded === props.id}
            onChange={handleChange(props.id)}
            disabled={props.disabled}
          >
            <AccordionSummary
              id={props.to}
              classes={{
                root: classNames(styles['accordion-summary'], {
                  [styles['accordion-summary-active']]: location.pathname.includes(
                    props.to,
                  ),
                }),
                content: classNames(styles['accordion-summary-content'], {
                  [styles['accordion-summary-content-row']]: props.parent,
                }),
              }}
              aria-disabled={props.disabled}
            >
              {props.parent ? (
                <NavLink
                  to={props.to}
                  end
                  onClick={event => event.stopPropagation()}
                  className={({ isActive }) =>
                    classNames(
                      styles['accordion-panel-link'],
                      isActive && styles['accordion-panel-link-active'],
                    )
                  }
                  data-aria-expanded={open}
                >
                  <AppIcon icon={props.icon} size={16} />
                  <span className={styles['link-title']}>{props.title}</span>
                </NavLink>
              ) : (
                <span className="flex flex-row items-center gap-x-4">
                  <AppIcon icon={props.icon} size={16} />
                  <span className={styles['link-title']}>{props.title}</span>
                </span>
              )}
            </AccordionSummary>
            <AccordionDetails
              classes={{ root: classNames(styles['accordion-details']) }}
            >
              {props.children.map(({ title, to, icon }) => (
                <NavLink
                  to={to}
                  key={to}
                  className={({ isActive }) =>
                    classNames(styles.link, {
                      [styles['link-active']]: isActive,
                    })
                  }
                  data-aria-expanded={open}
                >
                  {icon && <AppIcon icon={icon} size={16} />}
                  <span className={styles['link-title']}>{title}</span>
                </NavLink>
              ))}
            </AccordionDetails>
          </Accordion>
        )
      ) : (
        <NavLink
          to={props.to}
          className={({ isActive }) =>
            classNames(styles.link, {
              [styles['link-active']]: isActive,
              [styles['link-hidden']]: props.hidden,
            })
          }
          key={props.to}
          data-aria-expanded={open}
          aria-disabled={props.disabled}
          end={props.to === '/'}
          data-testid={`navigation-sidebar-link-${props.id}`}
        >
          {open ? (
            <AppIcon icon={props.icon} size={16} />
          ) : (
            <Tooltip title={props.title} placement="right">
              <AppIcon icon={props.icon} size={16} />
            </Tooltip>
          )}
          <span className={styles['link-title']} aria-hidden={!open}>
            {props.title}&nbsp;
            {open && props.description}
          </span>
          {open && props.adornment}
        </NavLink>
      )}
      {props.separator && <hr className={styles['list-divider']} />}
      {!props.separator && props.gap && <hr className={styles['list-divider']} />}
    </Fragment>
  );

  const renderButton = (
    props: NavigationButtonElement,
    key: number,
    open: boolean,
  ) => (
    <Fragment key={key}>
      {props.gap && <hr className={styles['list-divider']} />}
      <div
        className={classNames(styles.link, {
          [styles['link-hidden']]: props.hidden,
        })}
        data-aria-expanded={open}
        data-testid={`navigation-sidebar-button-${props.id}`}
      >
        {props.component}
      </div>
      {props.separator && <hr className={styles['list-divider']} />}
      {!props.separator && props.gap && <hr className={styles['list-divider']} />}
    </Fragment>
  );

  const navigationLinks = useMemo<NavigationLink[]>(() => {
    const isManageAccessEnv = isAnyBuildEnv([BuildEnv.LOCAL, BuildEnv.DEVELOPMENT]);

    return [
      {
        id: 'nuvara-360',
        type: 'section-title',
        title: t('glossary.nuvara-360', { ns: 'glossary' }),
      },
      {
        id: 'aggregation',
        type: 'link',
        icon: 'LayoutGrid',
        to: '/aggregation',
        title: t('glossary.summary', { ns: 'glossary' }),
        children: [],
      },
      {
        id: 'investment',
        type: 'link',
        icon: 'TrendingUp',
        to: '/aggregation/investment',
        title: t('glossary.investments', { ns: 'glossary' }),
        children: [],
      },
      {
        id: 'illiquids',
        type: 'link',
        icon: 'Building',
        to: '/aggregation/illiquids',
        title: t('glossary.non-liquid-assets', { ns: 'glossary' }),
        children: [],
      },
      {
        id: 'loans',
        type: 'link',
        icon: 'Landmark',
        to: '/aggregation/loans',
        title: t('glossary.debt', { ns: 'glossary' }),
        children: [],
      },
      {
        id: 'cashflow',
        type: 'link',
        icon: 'Wallet',
        to: '/aggregation/cashflow',
        title: t('glossary.liquidity', { ns: 'glossary' }),
        children: [],
      },
      {
        id: 'transactions',
        type: 'link',
        icon: 'Receipt',
        to: '/aggregation/transactions',
        title: t('glossary.transactions', { ns: 'glossary' }),
        children: [],
        separator: true,
      },
      {
        id: 'management',
        type: 'section-title',
        title: t('glossary.management', { ns: 'glossary' }),
      },
      {
        id: 'banks',
        title: t('glossary.accounts-and-connections', { ns: 'glossary' }),
        to: '/banks',
        children: [],
        icon: 'PlugZap',
        type: 'link',
      },
      {
        id: 'documents',
        title: t('glossary.documents', { ns: 'glossary' }),
        to: '/documents',
        children: [],
        icon: 'FileText',
        type: 'link',
      },
      !isManageAccessEnv && {
        id: 'accesses',
        title: t('glossary.accesses', { ns: 'glossary' }),
        to: '/advisors',
        children: [],
        icon: 'Users',
        type: 'link',
        separator: true,
      },
      isManageAccessEnv && {
        id: 'accesses',
        title: t('glossary.accesses', { ns: 'glossary' }),
        to: '/manage-access',
        children: [],
        icon: 'Users',
        type: 'link',
        separator: true,
      },
      showSilverAlpha && {
        id: 'silver-alpha',
        type: 'section-title',
        title: t('glossary.silver-alpha', { ns: 'glossary' }),
      },
      showSilverAlpha && {
        id: 'investment-services',
        title: t('glossary.investment-services', { ns: 'glossary' }),
        to: '/silver-alpha',
        children: [],
        icon: 'ShieldCheck',
        type: 'link',
      },
    ].filter(Boolean) as NavigationLink[];
  }, [showSilverAlpha, t]);

  return (
    <>
      <div
        className={styles.backdrop}
        onClick={() => onOpenChange(false)}
        data-aria-expanded={open}
        aria-hidden="true"
        data-testid="navigation-sidebar-backdrop"
      />
      <aside
        data-aria-expanded={open}
        className={classNames(styles.container, 'group')}
        data-scrollable
        data-testid="navigation-sidebar"
      >
        <div className={styles.header}>
          <AppLink
            to="/"
            className="hidden group-data-[aria-expanded=true]:block"
            data-testid="navigation-sidebar-logo"
          >
            {domain === 'nuvara' ? <LogoNuvara /> : <LogoCitec />}
          </AppLink>
          <button
            onClick={onDrawerChange}
            data-testid="navigation-sidebar-drawer-button"
          >
            <AppIcon icon="Menu" color="light" />
          </button>
        </div>
        <hr className={styles.divider} />
        <div>
          <nav
            className={styles.navigation}
            data-aria-expanded={open}
            data-testid="navigation-sidebar-navigation"
          >
            {navigationLinks.map((props, index) => {
              switch (props.type) {
                case 'section-title':
                  return renderSectionTitle(props, index);
                case 'link':
                  return renderLink(props, index, open, location);
                case 'button':
                  return renderButton(props, index, open);
                default:
                  return null;
              }
            })}
          </nav>
        </div>
      </aside>
    </>
  );
};
